import { useApp, useTriggerAction } from '@libs/core';
import { ActionActionTypeEnum, TWidget } from '@libs/types';
import SumsubWebSdk from '@sumsub/websdk-react';
import { AnyEventName, EventPayload, SnsError } from '@sumsub/websdk/types/types';
import { useCallback, useMemo, useRef } from 'react';
import { TKycProviderData, TKycSumsubCredentials } from '../../../types/data';
import { SUMSUB_STATUS_OUTCOME, SumsubStatus } from './sumsubOutcome';
import { useKycOutcome } from './useKycOutcome';

const TOKEN_REFRESH_TIMEOUT_MS = 30_000;

type TSumsubApplicantStatus = {
    reviewStatus: string;
    reviewResult?: {
        reviewAnswer: string;
        reviewRejectType?: string;
    };
};

type TSumsubProviderProps = {
    widget: TWidget<TKycProviderData>;
    credentials: TKycSumsubCredentials;
};

export const SumsubProvider = ({ widget, credentials }: TSumsubProviderProps) => {
    const { language } = useApp();
    const { triggerActionsByNameAndType } = useTriggerAction(widget);
    const { fireOutcome } = useKycOutcome(widget);
    const config = useMemo(() => ({ lang: credentials.locale ?? language }), [credentials.locale, language]);
    const submittedRef = useRef(false);

    const expirationHandler = useCallback(async (): Promise<string> => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            const timeout = new Promise<undefined>((resolve) => {
                timer = setTimeout(() => resolve(undefined), TOKEN_REFRESH_TIMEOUT_MS);
            });
            const refreshed = triggerActionsByNameAndType(ActionActionTypeEnum.onRefresh, 'refreshToken');
            const newWidget = await Promise.race([refreshed, timeout]);
            const value = newWidget?.data?.value as TKycProviderData['value'] | undefined;
            const token = (value?.credentials as TKycSumsubCredentials | undefined)?.accessToken ?? '';
            if (!token) fireOutcome(SUMSUB_STATUS_OUTCOME[SumsubStatus.FAILED]);
            return token;
        } catch (error) {
            console.error('Sumsub token refresh failed:', error);
            fireOutcome(SUMSUB_STATUS_OUTCOME[SumsubStatus.FAILED]);
            return '';
        } finally {
            clearTimeout(timer);
        }
    }, [triggerActionsByNameAndType, fireOutcome]);

    const onMessage = useCallback(
        (type: AnyEventName, payload: EventPayload<AnyEventName>) => {
            if (type === 'idCheck.onApplicantSubmitted' || type === 'idCheck.onApplicantResubmitted') {
                submittedRef.current = true;
                return;
            }
            if (type !== 'idCheck.onApplicantStatusChanged') return;
            const { reviewStatus, reviewResult } = payload as TSumsubApplicantStatus;
            let resolvedStatus: SumsubStatus | null = null;
            if (reviewStatus === 'pending') resolvedStatus = SumsubStatus.PENDING;
            else if (reviewStatus === 'completed' && reviewResult) {
                if (reviewResult.reviewAnswer === 'GREEN') resolvedStatus = SumsubStatus.APPROVED;
                else if (reviewResult.reviewAnswer === 'RED') {
                    if (reviewResult.reviewRejectType === 'FINAL') resolvedStatus = SumsubStatus.FINALLY_REJECTED;
                    else if (submittedRef.current) resolvedStatus = SumsubStatus.TEMPORARILY_DECLINED;
                }
            }

            if (resolvedStatus) fireOutcome({ ...SUMSUB_STATUS_OUTCOME[resolvedStatus], providerStatus: reviewStatus });
        },
        [fireOutcome],
    );

    const onError = useCallback(
        (error: SnsError) => {
            console.error('Sumsub SDK error:', error);
            fireOutcome(SUMSUB_STATUS_OUTCOME[SumsubStatus.FAILED]);
        },
        [fireOutcome],
    );

    return <SumsubWebSdk accessToken={credentials.accessToken} config={config} expirationHandler={expirationHandler} onError={onError} onMessage={onMessage} />;
};
